import type {
  FunctionRateView,
  ProjectLaborRates,
} from '@obraplan/core/application/use-cases/takeoff/labor-rate-use-cases';
import { QUARTERS_PER_HOUR } from '@obraplan/core/domain/shared/hours';
import { roundQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import {
  LABOR_SOURCE_LABEL,
  LABOR_WARNING_LABEL,
  type LaborBudgetSummary,
  type LaborRates,
  type LaborRateSource,
  type ResolvedLaborRate,
} from '@obraplan/core/domain/takeoff/labor-rate';
import {
  TOTAL_DECIMALS,
  type MaterialBudgetSummary,
} from '@obraplan/core/domain/takeoff/material-price';
import { mesPorExtenso } from '@/features/workforce/custo-da-folha';

/**
 * O custo da mão de obra da obra, como a aba de itens o lê e o grava (Fase 29, etapa 3 —
 * RN-399, D11 a D14).
 *
 * O que mora aqui é a parte sem React: a resposta do `GetProjectLaborRates` virando os mapas
 * que a regra do domínio lê (`LaborRates`), o selo da fonte, a base da conta e o custo direto
 * da obra. O gancho (`use-custo-da-mao-de-obra.ts`) só liga isto ao servidor.
 *
 * ## Por que a tela não refaz a cascata
 *
 * O R$/h da função sai da folha — obra, depois sistema, depois provisório (D11) — e a tela
 * **não vê a folha**: o servidor devolve a média da função, nunca o salário de ninguém (D14).
 * Por isso, gravado um provisório, quem diz o R$/h que passou a valer é uma leitura nova, e
 * não um palpite daqui sobre o que a cascata faria com ele.
 */

/** O custo da mão de obra da obra, já nos mapas da regra. */
export interface CustoDaMaoDeObra {
  /** O que `resolveLaborItemRate` e `priceLabor` leem. */
  readonly taxas: LaborRates;
  /** A função de cada chave `funcao:<id>` — com o provisório guardado, que vale só sem folha. */
  readonly funcoes: ReadonlyMap<string, FunctionRateView>;
  /** O mês da conta, `AAAA-MM` (RN-059). */
  readonly mes: string;
  /** As horas úteis do mês pelo calendário da obra, em quartos de hora. */
  readonly quartosDeHoraDoMes: number;
}

/**
 * A resposta do servidor vira os mapas da regra.
 *
 * Os provisórios são de duas origens e vão para o mesmo mapa: o da função (que o servidor
 * manda dentro dela, em `provisionalPerHour`) e o do item sem função (pela chave da
 * descrição). É o mapa que o diálogo lê para dizer o provisório guardado — inclusive o da
 * função que tem gente na folha, e por isso não o usa.
 */
export function custoDaObra(lido: ProjectLaborRates): CustoDaMaoDeObra {
  const provisorios = new Map<string, number>();
  for (const funcao of lido.functions) {
    if (funcao.provisionalPerHour !== undefined) {
      provisorios.set(funcao.key, funcao.provisionalPerHour);
    }
  }
  for (const provisorio of lido.provisionals) provisorios.set(provisorio.key, provisorio.perHour);
  const funcoes = new Map(lido.functions.map((funcao) => [funcao.key, funcao]));
  return {
    taxas: { functions: funcoes, provisionals: provisorios },
    funcoes,
    mes: lido.month,
    quartosDeHoraDoMes: lido.monthlyQuarterHours,
  };
}

/** O tom do selo: a folha da obra é o normal; a média do sistema é um empréstimo; o provisório pede atenção. */
export const TOM_DA_FONTE: Readonly<Record<LaborRateSource, 'neutral' | 'info' | 'warn'>> = {
  OBRA: 'neutral',
  SISTEMA: 'info',
  PROVISORIO: 'warn',
};

/**
 * O selo ao lado do R$/h: "Folha da obra · 3", "Média do sistema · 12", "Provisório".
 *
 * O número é de quantas pessoas é a média — uma média de uma pessoa é o salário dela, e quem
 * orça precisa saber disso antes de confiar no número.
 */
export function seloDaFonte(taxa: ResolvedLaborRate): string | undefined {
  if (taxa.source === undefined) return undefined;
  if (taxa.source === 'PROVISORIO') return LABOR_SOURCE_LABEL.PROVISORIO;
  return `${LABOR_SOURCE_LABEL[taxa.source]} · ${String(taxa.employees)}`;
}

const pessoas = (quantas: number): string =>
  `${String(quantas)} ${quantas === 1 ? 'funcionário ativo' : 'funcionários ativos'}`;

/**
 * De onde veio o R$/h, por extenso — a frase do diálogo.
 *
 * `semFuncao` é o item que ainda não aponta uma função (RN-252): não há folha para consultar,
 * e o provisório é a única fonte possível.
 */
export function deOndeVeio(taxa: ResolvedLaborRate, semFuncao: boolean): string {
  if (taxa.warning === 'NAO_E_HORA') {
    return 'O item não é medido em hora: R$/h vezes outra unidade daria um número sem sentido. Troque a unidade para HH no serviço.';
  }
  if (taxa.source === 'OBRA') {
    return `Média de ${pessoas(taxa.employees)} com salário nesta obra, com encargos.`;
  }
  if (taxa.source === 'SISTEMA') {
    return `Ninguém desta função na folha da obra: é a média de ${pessoas(taxa.employees)} com salário no sistema, com encargos.`;
  }
  if (taxa.source === 'PROVISORIO') {
    return semFuncao
      ? 'Provisório: o item não aponta uma função, e sem função não há folha para consultar.'
      : 'Provisório: ninguém desta função tem salário, na obra nem no sistema.';
  }
  return taxa.warning === undefined
    ? 'Sem custo.'
    : `Sem custo: ${LABOR_WARNING_LABEL[taxa.warning]}.`;
}

/**
 * A base da conta: "outubro/2026 · 176 h úteis pelo calendário da obra".
 *
 * O R$/h é do mês (RN-059): dezembro, com feriado, custa mais por hora que agosto. Sem dizer o
 * mês e o divisor, o número que muda na virada pareceria erro.
 */
export function baseDaConta(custo: CustoDaMaoDeObra): string {
  const mes = mesPorExtenso(custo.mes);
  if (custo.quartosDeHoraDoMes <= 0)
    return `${mes} · o calendário da obra não tem hora útil no mês`;
  const horas = (custo.quartosDeHoraDoMes / QUARTERS_PER_HOUR).toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  });
  return `${mes} · ${horas} h úteis pelo calendário da obra`;
}

/** O custo direto da obra: o que tem preço de material mais o que tem custo de mão de obra. */
export interface CustoDireto {
  /** Em reais, com duas casas. */
  readonly total: number;
  /** As linhas que ficaram fora da soma: material sem preço e mão de obra sem custo. */
  readonly foraDoTotal: number;
}

/**
 * Material + mão de obra (D15, sem BDI — D17).
 *
 * Soma só o que tem número, e diz quantas linhas ficaram de fora: o total sem elas é **menor**
 * do que a obra vai custar, e só serve lido com o aviso — a mesma regra do total de material.
 */
export function custoDireto(
  material: MaterialBudgetSummary,
  maoDeObra: LaborBudgetSummary,
): CustoDireto {
  return {
    total: roundQuantity(material.total + maoDeObra.total, TOTAL_DECIMALS),
    foraDoTotal: material.withoutPrice + maoDeObra.withoutRate,
  };
}

/**
 * Horas × R$/h, em reais com duas casas — a mesma conta de cada linha do `priceLabor`.
 *
 * É o que a parte de cada serviço e a linha da lista de itens mostram: com outra conta, a soma
 * das partes não fecharia com o total da função.
 */
export function custoDasHoras(horas: number, porHora: number): number {
  return roundQuantity(horas * porHora, TOTAL_DECIMALS);
}
